import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils/cn';

export interface MultiSelectOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

interface MultiSelectProps<T extends string> {
  options: MultiSelectOption<T>[];
  selected: Set<T>;
  onChange: (selected: Set<T>) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MultiSelect<T extends string>({
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  className,
  disabled = false,
}: MultiSelectProps<T>) {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const toggleOption = (value: T) => {
    const newSelected = new Set(selected);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    onChange(newSelected);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors',
          disabled
            ? 'cursor-default border-gray-200 bg-gray-50 text-gray-400 dark:border-white/5 dark:bg-white/[0.02] dark:text-gray-500'
            : 'cursor-pointer border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10',
          className
        )}
      >
        {placeholder}
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 min-w-[180px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-gray-900">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
            >
              <input
                type="checkbox"
                checked={selected.has(option.value)}
                onChange={() => toggleOption(option.value)}
                className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="flex-1 whitespace-nowrap text-gray-700 text-xs dark:text-gray-300">{option.label}</span>
              {option.count !== undefined && <span className="text-[11px] text-gray-400">{option.count}</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
